import { once } from 'lodash';
import { matchRoutes } from 'react-router-dom';

import { ChatComposerContextItemType } from '@/modules/common/Chat/components/ChatComposerContextBar';
import { ROUTE_KEYS } from '@/navigation/constants';
import { computedRoutes } from '@/navigation/routes';
import { isValidPulid } from '@/utils/pulid';

/**
 * The route-derived identity of the object the user is currently viewing.
 * `nodeId` identifies the object whose display name should be resolved.
 */
export interface ViewedContextResolution {
  type: ChatComposerContextItemType;
  /** GraphQL node ID of the viewed object, from the route params. */
  nodeId: string | null;
}

interface ViewedContextRoute {
  path: string;
  /**
   * null marks a route that must participate in matching but yields no
   * context: without it, a more generic dynamic pattern would capture the
   * page (e.g. the memos list matching documents/:documentId).
   */
  type: ChatComposerContextItemType | null;
  /** Route param holding the viewed object's node ID. */
  param: string | null;
}

// Every pattern ends in /* so it also matches sub-pages (tabs, trowsers)
// of the viewed object. react-router's ranking picks the most specific
// pattern, so e.g. an entity's document details resolves as the document,
// not the entity.
//
// Built lazily (not at module scope) because this module is in an import
// cycle with @/navigation/routes — routes.tsx renders layouts that
// transitively import this file — so `computedRoutes` may still be in its
// temporal dead zone while this module evaluates.
const getViewedContextRoutes = once((): ViewedContextRoute[] => [
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS]}/*`,
    type: 'document',
    param: 'documentId',
  },
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENT_DETAILS]}/*`,
    type: 'document',
    param: 'documentId',
  },
  // Memos share the document icon (see T2-10693's "Objects for Context").
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS]}/*`,
    type: 'document',
    param: 'memoId',
  },
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER_MEMO_DETAILS]}/*`,
    type: 'document',
    param: 'memoId',
  },
  // Exclusion: the memos LIST page would otherwise match
  // documents/:documentId with documentId="memos" (its static segments
  // outrank the dynamic pattern, and the dynamic :memoId pattern above still
  // outranks this splat for actual memo details pages).
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS]}/*`,
    type: null,
    param: null,
  },
  // The waterfall route segment between the tab and its sub-pages is a
  // __CONTAINER route (no addressable route key), so the pattern appends the
  // :waterfallId param to the tab path instead.
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB]}/:waterfallId/*`,
    type: 'waterfall',
    param: 'waterfallId',
  },
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_ENTITY_BASE]}/*`,
    type: 'entity',
    param: 'entityId',
  },
  {
    path: `${computedRoutes[ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2]}/*`,
    type: 'presentationSlide',
    param: 'presentationId',
  },
]);

/**
 * Resolves the current pathname to the product object being viewed, or null
 * when the page has no attachable context object (e.g. the overview page or
 * a document list). Presentation context also requires the presentation-tools
 * feature flag.
 */
export function resolveViewedContextFromPathname(
  pathname: string,
  presentationToolsEnabled = false
): ViewedContextResolution | null {
  const viewedContextRoutes = getViewedContextRoutes();
  const matches = matchRoutes(
    viewedContextRoutes.map((route) => ({ path: route.path })),
    { pathname }
  );
  const match = matches?.[0];
  if (!match) {
    return null;
  }

  const route = viewedContextRoutes.find(
    (candidate) => candidate.path === match.route.path
  );
  if (!route?.type) {
    return null;
  }
  if (route.type === 'presentationSlide' && !presentationToolsEnabled) {
    return null;
  }

  // A param-based route always defines its param, so a missing value means
  // the pattern didn't actually capture an object to attach.
  const nodeId = route.param ? (match.params[route.param] ?? null) : null;
  if (!nodeId) {
    return null;
  }

  // A splat pattern also matches list/static sub-pages of the same base path
  // (e.g. entities/list), capturing the static segment as the param. Only a
  // PULID can identify a node, so anything else is not an object to attach.
  if (!isValidPulid(nodeId)) {
    return null;
  }

  return { type: route.type, nodeId };
}
